"use client";
import { useUser } from "@/components/clients/LocaleToStorage";
import Input from "@/components/ui/input";
import { ValidateFormItem } from "@/components/ui/validate";
import { useI18n } from "@/messages/client";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { CitySelectForm, PrivacyCheckbox } from "../UserLayout/Register";
import FormLocation from "../Form/FormLocation";
import { useSearchParams } from "next/navigation";
import { CodeEmailInput } from "../BusinessConsult";
import FormApplication from "../Form/FormApplication";
import { Button } from "@/components/ui/button";
import { useCodeMessage } from "@/hooks/useCodeMessage";
import toast from "react-hot-toast";
import useSWRMutation from "swr/mutation";
import {
  getContactFormId,
  postFormApply,
  // postFormContent,
  // verifyEmailCode,
} from "@/server/form";
import { useSite } from "@/hooks/useSiteInfo";
import { isInteger } from "lodash-es";
import FormAreaCode from "../Form/FormAreaCode";
import useLocaleRouter from "@/hooks/useLocaleRouter";

interface RequestParams {
  name: string;
  phoneArea: string;
  phoneNumber: string;
  email: string;
  code: string;
  organization: string;
  position: string;
  location: string;
  locationName: string;
  city: string;
  cityName: string;
  materialNumber: string;
  application: string;
  applicationName: string;
  annualUsage: string;
  terminalApplication: string;
  applyNumber: string;
  privacy: boolean;
}

function FormLabel(props: {
  label: string;
  htmlForm?: string;
  required?: boolean;
  requiredImportant?: boolean;
}) {
  return (
    <label
      className="mb-2 block w-full text-sm md:mb-4 md:text-lg"
      htmlFor={props.htmlForm}>
      {props.requiredImportant && <span className="text-primary">*</span>}
      {props.label}
      {props.required && "*"}
    </label>
  );
}

export default function SampleRequest() {
  const t = useI18n();

  const site = useSite();

  const router = useLocaleRouter();

  const searchParams = useSearchParams();

  const { user, token } = useUser();

  const codeMessage = useCodeMessage();

  const [success, setSuccess] = useState(false);

  const methods = useForm<RequestParams>();

  const { handleSubmit, control, setValue } = methods;

  const { trigger, isMutating } = useSWRMutation(
    ["/api/form/create", site, token],
    async ([, site, token], { arg }: { arg: RequestParams }) => {
      const {
        code,
        // eslint-disable-next-line no-unused-vars
        privacy,
        ...formData
      } = arg;
      // const res = await verifyEmailCode({
      //   email: formData.email,
      //   code: code,
      // })
      // if (!res?.data) {
      //   return {
      //     errors: [
      //       {
      //         message: t("表单.邮箱验证码错误")
      //       }
      //     ]
      //   };
      // }
      const formId = await getContactFormId(site, "sample-request");
      if (!isInteger(formId)) {
        return formId;
      }

      return postFormApply({
        token: token as string,
        formId: formId as number,
        formData,
        code,
      });
    },
    {
      onError: () => {
        toast.error(t("表单.提交失败提示"));
      },
    }
  );

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (!token) {
        return;
      }
      const res = (await trigger(data)) as any;

      if ("errors" in res) {
        toast.error(
          codeMessage(res.errors[0].extensions?.code, res.errors[0].message)
        );

        return;
      }

      toast.success(t("样片申请.提交成功提示"), {
        duration: 3000,
      });
      setTimeout(() => {
        router.push("/");
      }, 3000);
      setSuccess(true);
    } catch (error) {
      toast.error(t("表单.提交失败提示"));
    }
  });

  const country = useWatch({
    control,
    name: "location",
  });

  const [renderDefaultCity, setRenderDefaultCity] = useState(false);

  useEffect(() => {
    if (user) {
      setValue("name", user.first_name);
      setValue("phoneArea", user.phone_area || "86");
      setValue("phoneNumber", user.phone_number);
      setValue("email", user.front_user_email);
      setValue("organization", user.organization);
      setValue("position", user.position);
      setValue("location", user.location);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user && country === "1" && !renderDefaultCity) {
      setTimeout(() => {
        setValue("city", user.city);
        setRenderDefaultCity(true);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, country, renderDefaultCity]);

  useEffect(() => {
    const materialNumber = searchParams.get("material_number");
    const applicationCategory = searchParams.get("application_category");

    if (materialNumber) {
      setValue("materialNumber", materialNumber);
    }

    if (applicationCategory) {
      setValue("application", applicationCategory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <div className="w-full">
      <FormProvider {...methods}>
        <form className="flex w-full flex-col" onSubmit={onSubmit}>
          <div className="mt-5 grid grid-cols-1 gap-y-4 md:mt-8 md:grid-cols-2 md:gap-x-20 md:gap-y-12">
            <div className="md:col-span-2">
              <Controller
                name="name"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t("注册模块.请输入姓名"),
                  },
                }}
                render={({ field }) => (
                  <ValidateFormItem name="name">
                    <FormLabel label={t("注册模块.姓名")} required />
                    <Input {...field} placeholder={t("注册模块.请输入姓名")} />
                  </ValidateFormItem>
                )}
              />
            </div>

            <div className="md:col-span-2">
              <FormLabel label={t("样片申请.联系方式")} required />
              <div className="grid grid-cols-1 gap-y-4 md:col-span-2 md:grid-cols-2 md:gap-x-20 md:gap-y-12">
                <Controller
                  name="phoneArea"
                  control={control}
                  render={({ field }) => (
                    <ValidateFormItem name="phoneArea">
                      <FormAreaCode field={field} />
                    </ValidateFormItem>
                  )}
                />

                <Controller
                  name="phoneNumber"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("样片申请.请输入联系电话"),
                    },
                    pattern: {
                      value: /^[1-9]\d{1,14}$/,
                      message: t("注册模块.请输入有效的手机号"),
                    },
                  }}
                  render={({ field }) => (
                    <ValidateFormItem name="phoneNumber">
                      <Input
                        {...field}
                        type="tel"
                        autoComplete="off"
                        placeholder={t("样片申请.请输入联系电话")}
                      />
                    </ValidateFormItem>
                  )}
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <FormLabel label={t("注册模块.邮箱地址")} required />
              <div className="grid grid-cols-1 gap-y-4 md:col-span-2 md:grid-cols-2 md:gap-x-20 md:gap-y-12">
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("注册模块.请输入邮箱地址"),
                    },
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: t("注册模块.请输入有效的邮箱地址"),
                    },
                  }}
                  render={({ field }) => (
                    <ValidateFormItem name="email">
                      <Input
                        {...field}
                        type="email"
                        placeholder={t("注册模块.请输入邮箱地址")}
                      />
                    </ValidateFormItem>
                  )}
                />

                <Controller
                  name="code"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("注册模块.请输入邮箱验证码"),
                    },
                  }}
                  render={({ field }) => (
                    <ValidateFormItem name="code">
                      <CodeEmailInput
                        value={field.value}
                        onChange={field.onChange}
                        placeholder={t("注册模块.邮箱验证码")}
                      />
                    </ValidateFormItem>
                  )}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-y-4 md:col-span-2 md:grid-cols-2 md:gap-x-20 md:gap-y-12">
              <Controller
                name="location"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t("表单.请选择国家"),
                  },
                }}
                render={({ field }) => (
                  <ValidateFormItem name="location">
                    <FormLabel label={t("注册模块.国家")} required />
                    <FormLocation
                      className="md:data-[placeholder]:text-[#E5E5E5]"
                      field={field}
                      isFetching
                      level="1"
                      placeholder={t("注册模块.国家")}
                      onLabelChange={(label) => {
                        setValue("locationName", label);
                        setValue("cityName", "");
                        setValue("city", "");
                      }}
                    />
                  </ValidateFormItem>
                )}
              />

              {country === "1" && (
                <Controller
                  name="city"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("表单.请选择城市"),
                    },
                  }}
                  render={({ field }) => (
                    <ValidateFormItem name="city">
                      <FormLabel label={t("注册模块.城市")} required />
                      <CitySelectForm
                        className="md:data-[placeholder]:text-[#E5E5E5]"
                        field={field}
                        required
                      />
                    </ValidateFormItem>
                  )}
                />
              )}
            </div>

            <Controller
              name="organization"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("注册模块.请输入公司"),
                },
              }}
              render={({ field }) => (
                <ValidateFormItem name="organization">
                  <FormLabel label={t("注册模块.公司")} required />
                  <Input {...field} placeholder={t("注册模块.请输入公司")} />
                </ValidateFormItem>
              )}
            />

            <Controller
              name="position"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("注册模块.请输入职位"),
                },
              }}
              render={({ field }) => (
                <ValidateFormItem name="position">
                  <FormLabel label={t("注册模块.职位")} required />
                  <Input {...field} placeholder={t("注册模块.请输入职位")} />
                </ValidateFormItem>
              )}
            />
          </div>

          <h2 className="mt-14 text-xl md:mt-20 md:text-2xl">
            <span className="border-b-4 border-primary pb-1">
              {t("样片申请.申请样片信息")}
            </span>
          </h2>

          <div className="mt-9 grid w-full grid-cols-1 gap-y-4 md:mt-14 md:gap-y-12">
            <Controller
              name="terminalApplication"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("样片申请.请输入应用"),
                },
              }}
              render={({ field }) => (
                <ValidateFormItem name="terminalApplication">
                  <FormLabel
                    label={t("样片申请.请输入您的在研产品的终端应用")}
                    requiredImportant
                  />
                  <Input
                    {...field}
                    suffix={
                      <img src="/icon/terminal2.svg" className="h-[18px]" />
                    }
                    placeholder={t("样片申请.请输入应用")}
                  />
                </ValidateFormItem>
              )}
            />

            <Controller
              name="materialNumber"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("样片申请.请输入料号"),
                },
              }}
              render={({ field }) => (
                <ValidateFormItem name="materialNumber">
                  <FormLabel label={t("样片申请.所需料号")} requiredImportant />
                  <Input
                    {...field}
                    suffix={
                      <img
                        src="/icon/material_number.svg"
                        className="h-[18px]"
                      />
                    }
                    placeholder="SC******"
                  />
                </ValidateFormItem>
              )}
            />

            <Controller
              name="application"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("样片申请.请选择具体应用场景"),
                },
              }}
              render={({ field }) => (
                <ValidateFormItem name="application">
                  <FormLabel label={t("样片申请.应用场景")} requiredImportant />
                  <FormApplication
                    className="md:data-[placeholder]:text-[#E5E5E5]"
                    field={field}
                    isFetching
                    placeholder={t("样片申请.请选择具体应用场景")}
                    onLabelChange={(label) => {
                      setValue("applicationName", label);
                    }}
                  />
                </ValidateFormItem>
              )}
            />

            <Controller
              name="applyNumber"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("样片申请.请输入数字"),
                },
                pattern: {
                  value: /^[1-9]\d*$/,
                  message: t("样片申请.请输入正整数"),
                },
              }}
              render={({ field }) => (
                <ValidateFormItem name="applyNumber">
                  <FormLabel label={t("样片申请.申请数量")} requiredImportant />
                  <Input
                    {...field}
                    suffix={<img src="/icon/edit.svg" className="h-[18px]" />}
                    placeholder={t("样片申请.请输入数字")}
                  />
                </ValidateFormItem>
              )}
            />

            <Controller
              name="annualUsage"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("样片申请.请输入数字"),
                },
                pattern: {
                  value: /^[1-9]\d*$/,
                  message: t("样片申请.请输入正整数"),
                },
              }}
              render={({ field }) => (
                <ValidateFormItem name="annualUsage">
                  <FormLabel
                    label={t("样片申请.预计年用量")}
                    requiredImportant
                  />
                  <Input
                    {...field}
                    suffix={<img src="/icon/edit.svg" className="h-[18px]" />}
                    placeholder={t("样片申请.请输入数字")}
                  />
                </ValidateFormItem>
              )}
            />
          </div>

          <Controller
            name="privacy"
            control={control}
            rules={{
              required: {
                value: true,
                message: t("注册模块.请阅读并勾选"),
              },
            }}
            render={({ field }) => (
              <ValidateFormItem
                className="mt-10 self-center md:mt-16"
                name="privacy">
                <PrivacyCheckbox
                  className="text-sm md:text-base"
                  value={field.value}
                  onChange={field.onChange}
                />
              </ValidateFormItem>
            )}
          />

          <Button
            type="submit"
            variant="outline"
            disabled={success}
            loading={isMutating}
            className="mt-5 self-center rounded border-zinc-400 bg-zinc-100 max-md:w-full md:mt-10 md:min-w-[180px]">
            {t("表单.提交申请")}
          </Button>
        </form>
      </FormProvider>
    </div>
  );
}
